/**
 * StoreBadgeSubscriber — shop counts for the Store sidebar.
 *
 * Connects to the `orders` dashboard hub and reads the Shop* block the Dashboards service
 * now exposes (non-vendor scoped, so vendor marketplace orders cannot inflate it).
 *
 * The REST baseline is not a fallback here, it is the primary source. The orders hub has
 * no badge consumers wired for the shop yet — the Shop* figures come from the hub's
 * metrics payload, not from BadgeDelta events — so this refetches on mount and on
 * reconnect rather than relying on a delta stream that does not exist. When those
 * consumers land, the onBadgeDelta path below starts applying without further change.
 */

import { useCallback, useEffect, useRef } from 'react';
import {
  useOrderService,
  useStoreCatalogueService,
  useDomainDashboard,
  type BadgeDeltaMessage,
  type BadgeSnapshotMessage,
} from '@actschurch/ccms-sdk';
import { useEnrichedAuth } from '@/hooks/useEnrichedAuth';
import { useStoreBadgesStore, type StoreBadgeCounts } from './storeBadgesStore';

const STORE_ROLES = ['ShopAdmin', 'Admin'];
const DEBUG = import.meta.env.DEV;
const log = (...args: any[]) => {
  if (DEBUG) console.log('[StoreBadgeSubscriber]', ...args); // eslint-disable-line no-console
};

export function StoreBadgeSubscriber() {
  const { hasAnyRole } = useEnrichedAuth();
  const enabled = hasAnyRole(STORE_ROLES);

  const orderService = useOrderService();
  const catalogue = useStoreCatalogueService();

  const setFromMetrics = useStoreBadgesStore((s) => s.setFromMetrics);
  const applyDelta = useStoreBadgesStore((s) => s.applyDelta);
  const reset = useStoreBadgesStore((s) => s.reset);

  const refetchBaseline = useCallback(async () => {
    try {
      // Both scoped to the merch catalogue. `nonVendorOnly` matters: without it the
      // unpaid count would include vendor marketplace orders the shop cannot act on.
      const [overview, byStatus, stock] = await Promise.all([
        orderService.statsOverview({ nonVendorOnly: true }),
        orderService.statsByStatus({ nonVendorOnly: true }),
        catalogue.listLowStock({ take: 1 }),
      ]);

      // Fulfilment state is per-status, not on the overview. OrderStatus lookup
      // (migration 020): 2 = Paid, 3 = Preparing, 4 = Ready For Pickup.
      const countFor = (statusId: number) =>
        (byStatus ?? []).find((s) => s.id === statusId)?.orderCount ?? 0;

      const counts: StoreBadgeCounts = {
        // Awaiting Payment (1) + Paid (2) — the working queue, not the whole book.
        newOrders: countFor(1) + countFor(2),
        unpaidOrders: overview?.unpaidOrders ?? 0,
        // Paid and Preparing are one queue — both mean "still to pack". Ready For Pickup
        // is its own badge because it is a different action: hand over, not pack.
        awaitingFulfilment: countFor(2) + countFor(3),
        readyForPickup: countFor(4),
        lowStock: stock?.lowStockCount ?? 0,
        outOfStock: stock?.outOfStockCount ?? 0,
      };

      log('baseline', counts);
      setFromMetrics(counts);
    } catch (err) {
      log('baseline fetch failed', err);
    }
  }, [orderService, catalogue, setFromMetrics]);

  useEffect(() => {
    if (!enabled) return;
    void refetchBaseline();
  }, [enabled, refetchBaseline]);

  const { isConnected, requestBadgeSnapshot } = useDomainDashboard('orders', {
    autoConnect: enabled,
    autoSubscribe: false,
    onBadgeSnapshot: (msg: BadgeSnapshotMessage<Partial<StoreBadgeCounts>>) => {
      // The hub names this domain "Orders" on snapshots and "orders" on deltas — match
      // case-insensitively rather than guessing which one this build sends.
      if (msg.domain?.toLowerCase() !== 'orders') return;
      log('snapshot', msg.snapshot);
      setFromMetrics(msg.snapshot ?? {});
    },
    onBadgeDelta: (msg: BadgeDeltaMessage<Partial<StoreBadgeCounts>>) => {
      if (msg.domain?.toLowerCase() !== 'orders') return;
      log('delta', msg.reason, msg.deltas);
      applyDelta(msg.deltas ?? {});
    },
  });

  // Refetch on RECONNECT, not on first connect — the mount effect above already did it.
  const wasConnectedRef = useRef(false);
  useEffect(() => {
    if (!enabled) return;
    if (isConnected && !wasConnectedRef.current) {
      wasConnectedRef.current = true;
      return;
    }
    if (isConnected && wasConnectedRef.current) {
      log('reconnected → refetch baseline');
      void refetchBaseline();
      void requestBadgeSnapshot();
    }
  }, [enabled, isConnected, refetchBaseline, requestBadgeSnapshot]);

  // A stale badge is worse than none: clear on unmount so a role change or sign-out
  // cannot leave another user looking at the previous one's counts.
  useEffect(() => () => reset(), [reset]);

  useEffect(() => {
    log('mounted', { enabled });
  }, [enabled]);

  return null;
}

export default StoreBadgeSubscriber;
